import type { UnpaidSession } from "@/types";
import { computeWorkContractWithholding, type WithholdingRates } from "@/lib/money/work-contract";
import { formatDateWithOptionsBs, formatTimeWithOptionsBs } from "@/lib/format";

export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatDateShort(iso: string): string {
  return formatDateWithOptionsBs(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Sarajevo",
  });
}

export function formatDateLong(date: Date): string {
  return formatDateWithOptionsBs(date, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatTime(iso: string): string {
  return formatTimeWithOptionsBs(iso, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Sarajevo",
  });
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** Bosnian plural form for "sesija": 1 sesija, 2-4 sesije, 5+ sesija. */
export function sessionWord(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "sesija";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "sesije";
  return "sesija";
}

/** "2 sesije · 1 grupna terapija": upisi u grupne terapije se broje odvojeno. */
export function itemSummary(items: UnpaidSession[]): string {
  const enrollments = items.filter((s) => s.kind === "enrollment").length;
  const sessions = items.length - enrollments;
  const parts: string[] = [];
  if (sessions > 0) parts.push(`${sessions} ${sessionWord(sessions)}`);
  if (enrollments > 0) {
    const mod10 = enrollments % 10;
    const mod100 = enrollments % 100;
    const word =
      mod10 === 1 && mod100 !== 11
        ? "grupna terapija"
        : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)
          ? "grupne terapije"
          : "grupnih terapija";
    parts.push(`${enrollments} ${word}`);
  }
  return parts.join(" · ");
}

export interface TherapistSessionGroup {
  therapistId: string;
  therapistName: string;
  sessions: UnpaidSession[];
  totalEarnings: number;
}

/** A payout can only belong to one therapist, so selections are split per therapist. */
export function groupSessionsByTherapist(
  sessions: UnpaidSession[],
): TherapistSessionGroup[] {
  const map = new Map<string, TherapistSessionGroup>();
  for (const session of sessions) {
    let group = map.get(session.therapistId);
    if (!group) {
      group = {
        therapistId: session.therapistId,
        therapistName: session.therapistName,
        sessions: [],
        totalEarnings: 0,
      };
      map.set(session.therapistId, group);
    }
    group.sessions.push(session);
    group.totalEarnings += session.therapistEarnings ?? 0;
  }
  return [...map.values()];
}

export interface PayoutPreview {
  /** Bruto isplate, isto zaokruženje kao `createPayoutCore`. */
  gross: number;
  /** Obustave kod ugovora o djelu; null kad stope nisu poznate. */
  totalWithheld: number | null;
  /** Iznos na račun; null kad obustave nisu poznate. */
  net: number | null;
  workContract: boolean;
  /** Stavke bez obračunate zarade: server ih odbija, pa se isplata ne smije potvrditi. */
  missingEarnings: number;
}

/**
 * Pregled jedne isplate (jedan terapeut) prije potvrde. Obustave računa ista
 * funkcija kao server (`computeWorkContractWithholding`) sa stopama koje je
 * server poslao, pa admin vidi neto koji će biti upisan.
 */
export function payoutPreview(items: UnpaidSession[], rates: WithholdingRates | null): PayoutPreview {
  const gross = Math.round(items.reduce((sum, s) => sum + (s.therapistEarnings ?? 0), 0) * 100) / 100;
  const missingEarnings = items.filter((s) => s.therapistEarnings == null).length;
  const workContract = items.some((s) => s.payoutMethod === "work_contract");
  if (!workContract) return { gross, totalWithheld: 0, net: gross, workContract, missingEarnings };
  if (!rates) return { gross, totalWithheld: null, net: null, workContract, missingEarnings };
  const w = computeWorkContractWithholding(gross, rates);
  return { gross, totalWithheld: w.totalWithheld, net: w.net, workContract, missingEarnings };
}
